import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

const LS_KEY = "choice-ay-admin-auth";

/**
 * Decode a JWT payload without verifying its signature.
 * Verification still happens server-side on every authed request \u2014 this
 * client-side check only proves "is this token obviously expired?" so we
 * can purge stale sessions before sending them.
 */
function decodeJwtPayload(token) {
    try {
        const parts = token.split(".");
        if (parts.length !== 3) return null;
        const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
        const pad = b64.length % 4 ? b64 + "=".repeat(4 - (b64.length % 4)) : b64;
        const json = decodeURIComponent(
            atob(pad)
                .split("")
                .map(
                    (c) =>
                        "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2),
                )
                .join(""),
        );
        return JSON.parse(json);
    } catch (err) {
        console.warn("Auth: failed to decode JWT payload:", err);
        return null;
    }
}

function isTokenExpired(token) {
    const p = decodeJwtPayload(token);
    if (!p?.exp) return false;
    return Date.now() / 1000 >= Number(p.exp);
}

/**
 * SECURITY NOTE \u2014 token storage:
 *
 * Admin JWTs are persisted in localStorage so Riley stays signed in between
 * visits. This is the standard pattern for SPA-only stacks, but it does mean
 * the token is reachable to any JS that runs on the page. We mitigate this by:
 *   - Short token lifetime (24h, controlled by `JWT_EXPIRES_MIN`)
 *   - Auto-purging the stored token the moment we detect it has expired
 *   - Never logging the token, never including it in URLs
 *   - Strict CORS on the backend
 *
 * If you ever expose this to less-trusted operators, migrate to an httpOnly
 * cookie + CSRF token flow on a custom backend route (requires server-side
 * cookie middleware + a same-site policy).
 */
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        try {
            const raw = localStorage.getItem(LS_KEY);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (parsed?.token && parsed?.user) {
                    if (isTokenExpired(parsed.token)) {
                        localStorage.removeItem(LS_KEY);
                    } else {
                        setUser(parsed.user);
                        setToken(parsed.token);
                    }
                }
            }
        } catch (err) {
            console.warn("Auth: failed to load session from storage:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    const login = useCallback(async (email, password) => {
        const { data } = await axios.post(`${API}/admin/login`, { email, password });
        setUser(data.user);
        setToken(data.access_token);
        try {
            localStorage.setItem(
                LS_KEY,
                JSON.stringify({ user: data.user, token: data.access_token }),
            );
        } catch (err) {
            console.warn("Auth: failed to persist session:", err);
        }
        return data;
    }, []);

    const logout = useCallback(() => {
        setUser(null);
        setToken(null);
        try {
            localStorage.removeItem(LS_KEY);
        } catch (err) {
            console.warn("Auth: failed to clear stored session:", err);
        }
    }, []);

    const authedFetch = useCallback(
        (path, opts = {}) => {
            const headers = {
                "Content-Type": "application/json",
                ...(opts.headers || {}),
                Authorization: token ? `Bearer ${token}` : "",
            };
            return axios({
                url: `${API}${path}`,
                method: opts.method || "GET",
                headers,
                data: opts.data,
            });
        },
        [token],
    );

    const value = useMemo(
        () => ({ user, token, loading, login, logout, authedFetch, API }),
        [user, token, loading, login, logout, authedFetch],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
    return ctx;
};

export default AuthContext;
